import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import { useQuery, gql } from '@apollo/client';
import {
  showNotification,
  NOTIFICATION_TYPE,
  PLATFORM_THEME_COLORS,
} from '@thinknet-gmbh/platform-common-ui';

export const GET_NOTIFICATIONS = gql`
  query getNotifications($queryOptions: QueryOptionsInput!) {
    getNotifications(queryOptions: $queryOptions) {
      collection {
        _id
        translationPath
        translationVariables
        isReadByAccount
        createdAt
      }
      totalCount
      unreadCount
    }
  }
`;

export const NOTIFICATION_ADDED_SUBSCRIPTION = gql`
  subscription notificationAdded {
    notificationAdded {
      _id
      translationPath
      translationVariables
    }
  }
`;

export const useGetNotifications = ({
  pageSize,
  skipPopupNotifications,
}: {
  pageSize: number;
  skipPopupNotifications: boolean;
}) => {
  const { i18n } = useTranslation();
  const [currentPage, setPage] = useState(1);

  const { data, loading, subscribeToMore } = useQuery(GET_NOTIFICATIONS, {
    variables: { queryOptions: { limit: pageSize, skip: pageSize * currentPage - pageSize } },
    fetchPolicy: 'network-only',
  });

  const { collection = [], totalCount = 0, unreadCount = 0 } = data?.getNotifications || {};

  useEffect(() => {
    subscribeToMore({
      document: NOTIFICATION_ADDED_SUBSCRIPTION,
      updateQuery: (prev, { subscriptionData }) => {
        const { collection = [], totalCount = 0, unreadCount = 0 } = prev?.getNotifications || {};
        const newNotification = subscriptionData?.data?.notificationAdded;

        if (!newNotification) {
          return prev;
        }
        if (!skipPopupNotifications) {
          const variablesKeys = Object.keys(newNotification.translationVariables);
          const values = variablesKeys.reduce((result, key) => {
            return Object.assign(result, {
              [key]: newNotification.translationVariables[key]
                ? newNotification.translationVariables[key]
                : i18n.t('notifications:titles.deleted'),
            });
          }, {});

          showNotification({
            type: NOTIFICATION_TYPE.info,
            message: i18n.t('notifications:titles.newNotification'),
            description: (
              <Trans
                i18nKey={newNotification.translationPath}
                values={values}
                components={{
                  target: <span style={{ color: PLATFORM_THEME_COLORS.primary.blue }} />,
                }}
              />
            ),
          });
          const resultCollection: any[] = [newNotification, ...collection];

          if (collection.length >= pageSize) {
            resultCollection.pop();
          }

          return {
            getNotifications: {
              collection: resultCollection,
              totalCount: totalCount + 1,
              unreadCount: unreadCount + 1,
            },
          };
        }
      },
    });
  }, [subscribeToMore, pageSize, i18n, skipPopupNotifications]);

  return {
    notifications: collection,
    unreadCount: unreadCount,
    loading,
    paginationConfig: {
      total: totalCount,
      pageSize,
      current: currentPage,
      onChange: (page: number) => setPage(page),
    },
  };
};
